import React from 'react';
import { LeadResponse } from '../../../types/lead-responses';

interface OwnershipInfoProps {
  leadResponse: LeadResponse & {
    company?: {
      name: string;
      company_admin_user_id?: string;
    };
    assigned_csr_user?: {
      full_name: string;
      email: string;
    };
    users?: {
      full_name: string;
      email: string;
      billing_plan?: string;
    };
  };
  userRole: string | null;
}

const OwnershipInfo: React.FC<OwnershipInfoProps> = ({ leadResponse, userRole }) => {
  const getPlanDisplay = (planType: string | null) => {
    switch (planType) {
      case 'junior_sdr':
        return 'Junior SDR ($400/mo)';
      case 'top_qualifier_sdr':
        return 'Top Qualifier ($2000/mo)';
      default:
        return planType || 'Unknown';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'submitted':
        return 'bg-yellow-100 text-yellow-800';
      case 'preparing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (userRole === 'csr' || userRole === 'admin') {
    // CSR view - show client/user information
    const campaignUser = leadResponse.campaign?.user;
    const company = leadResponse.company;
    const campaign = leadResponse.campaign;

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <h3 className="font-medium text-gray-900 mb-3 flex items-center">
          <span className="mr-2">🏢</span> Client & Campaign Info
        </h3>
        <div className="space-y-2 text-sm">
          {campaignUser && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Client:</span>
                <span className="font-medium">{campaignUser.full_name || campaignUser.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{campaignUser.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Plan:</span>
                <span className="font-medium">{getPlanDisplay(campaignUser.plan_type)}</span>
              </div>
            </>
          )}
          {company && (
            <div className="flex justify-between">
              <span className="text-gray-600">Company:</span>
              <span className="font-medium">{company.name}</span>
            </div>
          )}
          {campaign && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Campaign:</span>
                <span className="font-medium">{campaign.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Campaign Status:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(campaign.status)}`}>
                  {campaign.status}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  } else {
    // Regular user view - show CSR information
    const assignedCSR = leadResponse.campaign?.csr || leadResponse.assigned_csr_user;
    const campaign = leadResponse.campaign;

    return (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
        <h3 className="font-medium text-gray-900 mb-3 flex items-center">
          <span className="mr-2">📋</span> Campaign & Support Info
        </h3>
        <div className="space-y-2 text-sm">
          {campaign && (
            <div className="flex justify-between">
              <span className="text-gray-600">Campaign:</span>
              <span className="font-medium">{campaign.name}</span>
            </div>
          )}
          {assignedCSR ? (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Your CSR:</span>
                <span className="font-medium">{assignedCSR.full_name || 'Not assigned'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">CSR Email:</span>
                <span className="font-medium">{assignedCSR.email}</span>
              </div>
            </>
          ) : (
            <div className="text-gray-500 italic">No CSR assigned yet</div>
          )}
        </div>
      </div>
    );
  }
};

export default OwnershipInfo;